// Zod DTOs for the `/v1/users` PLATFORM USERS surface (P3-18, spec §3.3
// `User`/`Subscription` models, §6.5 error contract) — the ADMIN-only,
// platform-WIDE user directory (every role, incl. Admins), distinct from
// `./roster.ts`'s `/v1/students` (the TEACHER roster — STUDENT rows only)
// and the (not-yet-built) `/v1/teachers`. Single source of truth for
// `apps/api`'s validators + the OpenAPI generator, mirroring `./roster.ts`'s
// header convention for this surface.
//
// ─── Two SEPARATE axes: `status` vs `billingStatus` ────────────────────────
// `status` is the ACCOUNT state (`User.status` — ACTIVE|INVITED|SUSPENDED|
// PENDING_VERIFICATION). `billingStatus` is the SUBSCRIPTION state, derived
// from the caller's most relevant `Subscription` row (see
// `../../../apps/api/src/services/users.ts`'s `pickCurrentSubscription` doc
// comment for the derivation rule) — `NONE` when the user has no
// `Subscription` row at all (true for every ADMIN/TEACHER, and for a STUDENT
// who has never checked out). These NEVER collapse into one field.
import { z } from 'zod';
import { emailSchema, idSchema, nameSchema, passwordSchema, paginationQuerySchema } from './primitives';

// ─── Shared value sets ──────────────────────────────────────────────────────

export const platformUserRoleSchema = z.enum(['ADMIN', 'TEACHER', 'STUDENT']);
export type PlatformUserRole = z.infer<typeof platformUserRoleSchema>;

export const platformUserStatusSchema = z.enum([
  'ACTIVE',
  'INVITED',
  'SUSPENDED',
  'PENDING_VERIFICATION',
]);
export type PlatformUserStatus = z.infer<typeof platformUserStatusSchema>;

// ─── GET /v1/users ───────────────────────────────────────────────────────────

export const platformUserListQuerySchema = paginationQuerySchema.extend({
  /** Case-insensitive substring match against `email`/`name` — optional. */
  q: z.string().trim().min(1).max(200).optional(),
  role: platformUserRoleSchema.optional(),
  /** ACCOUNT status filter — see this file's header for why this is a
   * SEPARATE axis from `plan`/billing below. */
  status: platformUserStatusSchema.optional(),
  /** Filters on the `scopeType` (GLOBAL|LEVEL|SUBJECT) of ANY `Subscription`
   * row the user holds — see `../../../apps/api/src/services/users.ts`'s
   * header for why `plan` is a derived label rather than a stored column
   * (no dedicated `Plan` model exists in `prisma/schema.prisma` yet). */
  plan: z.string().trim().min(1).max(60).optional(),
});
export type PlatformUserListQuery = z.infer<typeof platformUserListQuerySchema>;

export const platformUserListItemSchema = z.object({
  id: idSchema,
  name: z.string(),
  email: z.string(),
  role: platformUserRoleSchema,
  // `z.string()` (not `platformUserStatusSchema`) mirrors `./roster.ts`'s
  // `studentResponseSchema` precedent — the DB value is already constrained
  // by `UserStatus` (spec §3.3), this stays a plain string on the wire/DTO
  // boundary so `../../../apps/api/src/services/users.ts`'s mapper (which
  // returns the raw `User.status` column) assigns here without a redundant
  // re-narrowing cast at every call site.
  status: z.string(),
  plan: z.string().nullable(),
  billingStatus: z.string(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformUserListItem = z.infer<typeof platformUserListItemSchema>;

/** `Page<PlatformUserListItem>` — cursor pagination PLUS `totalEstimate` so
 * the admin UI can render a "1-N of M" counter (spec AC). Deliberately a
 * separate envelope from `./primitives.ts`'s `paginatedResponseSchema`
 * (`{items, nextCursor}` only) rather than widening that shared helper —
 * every OTHER existing consumer of `paginatedResponseSchema` has no
 * "of M" counter requirement, and adding one there would force every list
 * endpoint in the codebase to start computing a count it doesn't need. */
export const platformUserPageSchema = z.object({
  items: z.array(platformUserListItemSchema),
  nextCursor: z.string().nullable(),
  totalEstimate: z.number().int().nonnegative(),
});
export type PlatformUserPage = z.infer<typeof platformUserPageSchema>;

// ─── GET /v1/users/:id ───────────────────────────────────────────────────────
// Response-only shapes — mirror `../../../apps/api/src/services/users.ts`'s
// `toPlatformUserDetail` mapper 1:1.

export const platformSubscriptionItemSchema = z.object({
  id: z.string(),
  scopeType: z.string(),
  scopeId: z.string().nullable(),
  provider: z.string(),
  providerRef: z.string().nullable(),
  status: z.string(),
  amountMinor: z.number(),
  currency: z.string(),
  currentPeriodEnd: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformSubscriptionItem = z.infer<typeof platformSubscriptionItemSchema>;

export const platformUserDetailSchema = platformUserListItemSchema.extend({
  emailVerifiedAt: z.string().nullable(),
  // `enrollments[]` REMOVED (feat/remove-enrollment-access-path) — no
  // frontend consumer ever read this field off `GET /v1/users/:id`
  // (`RosterView.vue`'s enroll dialog reads `GET
  // /v1/students/:id/enrollments` instead, a different endpoint, kept
  // read-only). StudentPackage is now the sole access-grant mechanism;
  // see `apps/api/src/services/entitledScopes.ts`'s header.
  subscriptions: z.array(platformSubscriptionItemSchema),
});
export type PlatformUserDetail = z.infer<typeof platformUserDetailSchema>;

// ─── POST /v1/users/invite ───────────────────────────────────────────────────
// Unlike `./roster.ts`'s `studentInviteRequestSchema` (STUDENT-only, the
// roster surface's sole concern), this accepts `role` — any of
// ADMIN|TEACHER|STUDENT (spec AC). The response is a `PlatformUserListItem`
// — the invite token is NEVER echoed in the body (spec AC; it is emailed via
// the already-wired `email.invite` queue message / stored server-side only
// as a hashed `VerificationToken` row, mirroring `../../../apps/api/src/
// routes/students.ts`'s `POST /v1/students` precedent).

export const userInviteRequestSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  role: platformUserRoleSchema,
});
export type UserInviteRequest = z.infer<typeof userInviteRequestSchema>;

// ─── POST /v1/users/:id/password-reset ────────────────────────────────────
// feat/teacher-password-reset (security-review-specified design), AMENDED
// by the 2026-09-12 PO ruling: ADMIN is now a password-reset super-user
// (any target, incl. another ADMIN, incl. self); TEACHER is unchanged —
// still only a STRICTLY-lower-ranked user's password
// (`@eskayscience/shared/enums`'s `canAdminSuperuserResetPassword`, which
// falls back to `outranksForPasswordReset` — ADMIN=2 > TEACHER=1 >
// STUDENT=0, one ordinal comparison, never a pairwise allowlist — for
// non-ADMIN actors). Deliberately has NO `role`/target-identity field — the target
// is ALWAYS the `:id` route param, and its role is ALWAYS read fresh from
// the DB server-side (`../../../apps/api/src/routes/users.ts`'s handler),
// never client-supplied, so a caller cannot smuggle a claimed role into the
// body to influence the authorization decision. `password` is
// caller-supplied (mirrors `./roster.ts`'s `studentDirectCreateRequestSchema`
// precedent for "admin sets a real, immediately-usable password server-side"
// rather than this route inventing a different UX pattern).
export const adminPasswordResetRequestSchema = z.object({
  password: passwordSchema,
});
export type AdminPasswordResetRequest = z.infer<typeof adminPasswordResetRequestSchema>;

// ─── PATCH /v1/users/:id/status ────────────────────────────────────────────
// docs/design/admin-account-suspension.md (design locked 2026-09-11): a
// sub-resource of this same ADMIN-only surface, same shape/precedent as
// `adminPasswordResetRequestSchema` above (privileged, single-purpose,
// rank-checked). Deliberately `z.enum(['ACTIVE', 'SUSPENDED'])` — NOT the
// full `platformUserStatusSchema` — so `INVITED`/`PENDING_VERIFICATION`
// (lifecycle states owned by the invite/verify flows) can never be set via
// this route; flipping a `PENDING_VERIFICATION` account straight to
// `ACTIVE` here would bypass email verification. `reason` is optional and
// audit-only (never persisted on the `User` row itself — see the design
// doc's §4 "no migration needed" ruling).
export const userStatusUpdateRequestSchema = z.object({
  status: z.enum(['ACTIVE', 'SUSPENDED']),
  reason: z.string().trim().max(500).optional(),
});
export type UserStatusUpdateRequest = z.infer<typeof userStatusUpdateRequestSchema>;
